import { faTrain } from '@fortawesome/pro-regular-svg-icons';
import { type TrainPathData } from './types';
import TrainPriceLoader from './TrainPriceLoader';
import { TripSearchForm } from '@search/trip/TripSearchForm';
import {
    type SellType,
    SellTypeCode,
    type TrainLocation,
    trainSellTypes,
    type TrainUrlParams,
} from '@utils/train';
import {
    type PaxCount,
    PaxType,
    type ReserveInfo,
    SearchMode,
} from '@utils/types';
import {
    ReserveType,
    ViewNotFoundError,
    ViewRedirectError,
} from '@utils/itinerary';
import { type UrlStringParams } from '@search/types';
import { TripType } from '@utils/trip';
import foreignStations from '@static/foreign_train_stations';
import type {
    TripShareInfo,
    TripShareLocations,
} from '@itinerary/trip/TripModuleTypes';
import { TrainApiApiGetNearbyRoutesPost } from '@clients/TrainApi';

export class TrainSearchForm extends TripSearchForm<
    TrainLocation,
    TrainPathData
> {
    trainStations: TrainLocation[] = [];
    sellType: SellType = trainSellTypes[0];
    exclusive = false;
    override get foreignOrigins() {
        return foreignStations;
    }

    get maxPassengersCount() {
        if (this.sellType.code === 4) {
            return 1;
        }
        return 12;
    }

    override async loadStaticLocations() {
        if (!this.trainStations.length) {
            this.isLoadingLocations = true;
            this.trainStations = (
                await import('@static/domestic_train_stations')
            ).default;
            this.isLoadingLocations = false;
        }
    }

    override get foreignDestinations() {
        return foreignStations;
    }

    override get domesticOrigins() {
        return this.trainStations.map(station => ({
            ...station,
            code: station.code.toString(),
        }));
    }

    override get domesticDestinations() {
        return this.domesticOrigins;
    }

    async findCodeBaseOnStationId(
        stationId: number,
        options: { target: 'origin' | 'destination'; mode: SearchMode }
    ): Promise<string | undefined> {
        await this.searchLocations(stationId.toString(), options);
        return this.trainStations.find(s => s.stationId === stationId)?.code;
    }

    override async migrateViewPath(pathData: TrainPathData) {
        const {
            origin,
            destination,
            departureDate,
            returnDate,
            adultCount,
            childCount,
            infantCount,
            exclusive,
            sellTypeCode,
        } = pathData;
        const originName = await this.findCodeBaseOnStationId(Number(origin), {
            target: 'origin',
            mode: SearchMode.Domestic,
        });
        const destinationName = await this.findCodeBaseOnStationId(
            Number(destination),
            { target: 'destination', mode: SearchMode.Domestic }
        );
        const { fullPath, queries } = this.generateViewUrl({
            origin: originName,
            destination: destinationName,
            departureDate,
            returnDate,
            adultCount,
            childCount,
            infantCount,
            sellTypeCode,
            exclusive,
        });
        return Promise.reject(
            new ViewRedirectError('Path not valid', fullPath, queries)
        );
    }

    override async loadViewUrlParams(params: TrainUrlParams) {
        await super.loadViewUrlParams(params);
        this.setSellTypeCode(params.sellTypeCode || SellTypeCode.Family);
        this.exclusive = params.exclusive || false;
    }

    override async searchLocations(
        searchKeyword: string,
        options: { target: 'origin' | 'destination'; mode: SearchMode }
    ) {
        await this.loadStaticLocations();
        await super.searchLocations(searchKeyword, options);
    }

    override generateViewQueries(params: UrlStringParams<TrainUrlParams>) {
        const original = super.generateViewQueries(params);
        const query = new URLSearchParams(original);
        if (params.sellTypeCode && params.sellTypeCode !== '3') {
            query.append('sellTypeCode', params.sellTypeCode);
        }
        if (params.exclusive && params.exclusive !== '0') {
            query.append('exclusive', params.exclusive);
        }
        return query;
    }

    override generateViewUrl(params: UrlStringParams<TrainUrlParams>) {
        const query = this.generateViewQueries(params);
        const q = query.toString();
        return {
            fullPath: `/trains/${params.origin}-${params.destination}${
                q ? `?${q}` : ''
            }`,
            queries: Object.fromEntries(query.entries()),
        };
    }

    override getPriceLoader(
        origin: TrainLocation,
        destination: TrainLocation,
        tripType: TripType
    ): TrainPriceLoader {
        return new TrainPriceLoader({
            tripType,
            from: `${origin.stationId}`,
            to: `${destination.stationId}`,
            adultCount: this.adultCount,
            childCount: this.childCount,
            infantCount: this.infantCount,
            priceTypeCode: `${this.sellType.code}`,
        });
    }

    override initPaxCounts: PaxCount[] = [
        {
            code: PaxType.Adult,
            title: 'بزرگسال',
            subtitle: 'بزرگتر از ۱۲ سال',
            count: 1,
        },
        {
            code: PaxType.Child,
            title: 'کودک',
            subtitle: 'بین ۲ الی ۱۲ سال',
            count: 0,
        },
        {
            code: PaxType.Infant,
            title: 'نوزاد',
            subtitle: 'کوچکتر از ۲ سال',
            count: 0,
        },
    ];

    override get capacityInfo(): {
        [key in PaxType]?: {
            count: number;
            min: number;
            max: number;
        };
    } {
        return {
            [PaxType.Adult]: {
                count: this.adultCount,
                min: 1,
                max:
                    this.maxPassengersCount -
                    this.childCount -
                    this.infantCount,
            },
            [PaxType.Child]: {
                count: this.childCount,
                min: 0,
                max:
                    this.maxPassengersCount -
                    this.adultCount -
                    this.infantCount,
            },
            [PaxType.Infant]: {
                count: this.childCount,
                min: 0,
                max: Math.min(
                    this.maxPassengersCount - this.adultCount - this.childCount,
                    this.adultCount
                ),
            },
        };
    }

    override updatePaxCount(params: { type: PaxType; count: number }) {
        const paxCount = this.capacityInfo[params.type];
        const initPaxCount = this.initPaxCounts.find(
            pax => pax.code === params.type
        );
        if (
            !initPaxCount ||
            !paxCount ||
            params.count > paxCount.max ||
            params.count < paxCount.min
        ) {
            if (params.type === PaxType.Infant) {
                throw new Error(
                    'تعداد نوزاد نمی‌تواند از تعداد بزرگسال بیشتر باشد.'
                );
            } else {
                throw new Error(
                    this.sellType.code === 4
                        ? 'حداکثر تعداد برای رزرو ۱ خودرو می‌باشد.'
                        : `بنابر دستور شرکت راه‌آهن حداکثر تعداد مسافران ${this.maxPassengersCount} نفر است.`
                );
            }
        }
        if (
            params.type === PaxType.Adult && // is adult
            paxCount.count > params.count && // is decreasing
            this.infantCount === this.adultCount // infant is already at max
        ) {
            throw new Error(
                'در صورت کاهش بزرگسال، شرط سقف تعداد نوزاد رعایت نمی‌شود.'
            );
        }
        if (
            this.totalCount === this.maxPassengersCount &&
            params.count > paxCount.count
        ) {
            throw new Error(
                this.sellType.code === 4
                    ? 'حداکثر تعداد برای رزرو ۱ خودرو می‌باشد.'
                    : `بنابر دستور شرکت راه‌آهن حداکثر تعداد مسافران ${this.maxPassengersCount} نفر است.`
            );
        }
        initPaxCount.count = params.count;
    }

    override popularDomesticOrigins = [
        { code: 'tehran', title: 'تهران', stationId: 1 },
        { code: 'mashhad', title: 'مشهد', stationId: 191 },
        {
            code: 'shahroud',
            title: 'شاهرود',
            stationId: 130,
        },
        { code: 'yazd', title: 'یزد', stationId: 219 },
        {
            code: 'zanjan',
            title: 'زنجان',
            stationId: 97,
        },
    ];

    override popularDomesticDestinations = this.popularDomesticOrigins;
    override popularForeignOrigins = [];
    override popularForeignDestinations = [];

    override get reserveInfo(): ReserveInfo {
        return {
            type: ReserveType.Train,
            title: 'قطار',
            nameE: 'قطاری',
            icon: faTrain,
        };
    }

    override getLocationKeywords(location: TrainLocation) {
        return [
            ...super.getLocationKeywords(location),
            `${location.stationId}`,
        ];
    }

    override get viewUrl() {
        return this.generateViewUrl({
            origin: this.origin?.code,
            destination: this.destination?.code,
            departureDate: this.formattedDepartureDate,
            returnDate:
                this.tripType === TripType.RoundTrip
                    ? this.formattedReturnDate
                    : undefined,
            adultCount: this.adultCount.toString(),
            childCount: this.childCount.toString(),
            infantCount: this.infantCount.toString(),
            sellTypeCode: this.sellType.code.toString(),
            exclusive: this.exclusive ? '1' : '0',
        }).fullPath;
    }

    override get serviceIndexUrl(): string {
        return '/train-ticket';
    }

    findStationIdBaseOnCode(code: string): number | undefined {
        return this.trainStations.find(s => s.code === code)?.stationId;
    }

    override async parsePath(pathData: TrainPathData): Promise<TrainUrlParams> {
        const { origin, destination } = pathData;
        await this.searchLocations(origin, {
            target: 'origin',
            mode: SearchMode.Domestic,
        });
        await this.searchLocations(destination, {
            target: 'destination',
            mode: SearchMode.Domestic,
        });
        const originCode = this.findStationIdBaseOnCode(origin);
        const destinationCode = this.findStationIdBaseOnCode(destination);
        if (!originCode || !destinationCode) {
            throw new ViewNotFoundError('origin or destination not found');
        }
        return {
            origin,
            destination,
            originCode,
            destinationCode,
            departureDate: pathData.departureDate
                ? parseDate(pathData.departureDate)!
                : this.defaultDepartureDate,
            sellTypeCode: Number(pathData.sellTypeCode) as SellTypeCode,
            exclusive: pathData.exclusive === '1',
            adultCount: Number(pathData.adultCount) || 1,
            childCount: pathData?.childCount
                ? parseInt(pathData.childCount)
                : 0,
            infantCount: pathData?.infantCount
                ? parseInt(pathData.infantCount)
                : 0,
            returnDate: pathData.returnDate
                ? parseDate(pathData.returnDate)!
                : undefined,
        };
    }

    override duplicateViewQueries(
        currentQueries: UrlStringParams<TrainUrlParams>,
        viewQueries: UrlStringParams<TrainUrlParams>
    ) {
        if (!super.duplicateViewQueries(currentQueries, viewQueries)) {
            return false;
        }
        if (
            (currentQueries.exclusive || '0') !== (viewQueries.exclusive || '0')
        ) {
            return false;
        }
        // noinspection RedundantIfStatementJS
        if (
            (currentQueries.sellTypeCode || SellTypeCode.Family) !==
            (viewQueries.sellTypeCode || SellTypeCode.Family)
        ) {
            return false;
        }
        return true;
    }

    setSellTypeCode(code: SellTypeCode) {
        this.sellType =
            trainSellTypes.find(sellType => sellType.code === code) ||
            this.sellType;
        if (this.sellType.code === SellTypeCode.Car) {
            this.initPaxCounts = [
                {
                    code: PaxType.Adult,
                    title: 'دستگاه',
                    subtitle: '',
                    count: 1,
                },
            ];
        } else {
            this.initPaxCounts = [
                {
                    code: PaxType.Adult,
                    title: 'بزرگسال',
                    subtitle: 'بزرگتر از ۱۲ سال',
                    count: this.adultCount,
                },
                {
                    code: PaxType.Child,
                    title: 'کودک',
                    subtitle: 'بین ۲ الی ۱۲ سال',
                    count: this.childCount,
                },
                {
                    code: PaxType.Infant,
                    title: 'نوزاد',
                    subtitle: 'کوچکتر از ۲ سال',
                    count: this.infantCount,
                },
            ];
        }
    }

    override async loadSuggestions(params: TripShareInfo & TripShareLocations) {
        try {
            const { data } = await TrainApiApiGetNearbyRoutesPost(
                {
                    getNearbyRoutesRequest: {
                        from: {
                            latitude: params.origin.latitude,
                            longitude: params.origin.longitude,
                        },
                        to: {
                            latitude: params.destination.latitude,
                            longitude: params.destination.longitude,
                        },
                        departureDate: dayConfig
                            .gregory(params.departureDate)
                            .format(formatter()),
                    },
                },
                {
                    timeout: 10000,
                }
            );

            const route = data.routes?.[0];
            if (!route?.fromID || !route.toID) {
                this.suggestionResult = null;
                return;
            }
            const origin = await this.findCodeBaseOnStationId(route.fromID, {
                mode: SearchMode.Domestic,
                target: 'origin',
            });
            const destination = await this.findCodeBaseOnStationId(route.toID, {
                mode: SearchMode.Domestic,
                target: 'destination',
            });
            if (!origin || !destination) {
                this.suggestionResult = null;
                return;
            }
            this.suggestionResult = {
                viewUrl: this.generateViewUrl({
                    origin,
                    destination,
                    departureDate: dayConfig
                        .jalali(params.departureDate)
                        .format(formatter()),
                    returnDate: params.returnDate
                        ? dayConfig
                              .jalali(params.returnDate)
                              .format(formatter())
                        : undefined,
                    adultCount: params.adultCount.toString(),
                    childCount: params.childCount.toString(),
                    infantCount: params.infantCount.toString(),
                }).fullPath,
                originTitle: route.fromName ?? '',
                destinationTitle: route.toName ?? '',
                minPrice: route.minPrice,
            };
        } catch (e) {
            this.suggestionResult = null;
            reportException(e);
        }
    }
}
